import React, { useEffect } from 'react'
import FlagProvider, { useUnleashContext } from '@unleash/proxy-client-react'
import type { IConfig } from 'unleash-proxy-client'

import { getEnv } from '@/utils/helpers'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

// Self-hosted Unleash server frontend API (not the Edge proxy) — same for
// every deployment, so only the API key comes from the environment.
const UNLEASH_URL = 'https://unleash.connect.promotal.com/api/frontend'

const apiKey = getEnv('VITE_UNLEASH_API_KEY')

/**
 * Unleash is optional: when the public API key is not configured, the
 * provider still mounts (so `useFlag` never crashes) but the client is
 * never started — every flag evaluates to its safe default (false).
 */
const isUnleashConfigured = Boolean(apiKey)

const config: IConfig = {
  url: UNLEASH_URL,
  // The SDK constructor rejects empty strings, so feed it an inert
  // placeholder when unconfigured — startClient is false then, no request
  // is made.
  clientKey: apiKey || 'offline-placeholder',
  appName: 'medconnect-professional',
  refreshInterval: 15, // seconds
}

/**
 * Pushes the authenticated user into the Unleash context so flags can target
 * by user / tenant / establishment, and resets it when the user logs out.
 */
function UnleashContextSync() {
  const updateContext = useUnleashContext()
  const authUser = useAppSelector(selectAuthUser)

  const userId = authUser?._id
  const etablissementId = authUser?.establishment?._id

  useEffect(() => {
    if (!isUnleashConfigured) return
    if (userId) {
      updateContext({
        userId,
        properties: {
          tenantId: getEnv('VITE_KEYCLOAK_REALM') ?? '',
          etablissementId: etablissementId ?? '',
        },
      })
    } else {
      updateContext({ userId: undefined, properties: {} })
    }
  }, [updateContext, userId, etablissementId])

  return null
}

type UnleashProviderProps = {
  children: React.ReactNode
}

export function UnleashProvider({ children }: UnleashProviderProps) {
  return (
    <FlagProvider config={config} startClient={isUnleashConfigured}>
      <UnleashContextSync />
      {children}
    </FlagProvider>
  )
}

export default UnleashProvider
